import { Component, Input, Output, EventEmitter, OnInit } from "@angular/core"
import { IWriter, ICustomer, IKeyword } from "graphql/queries/keyword.queries"
import { externalRoute } from '../external-routes'

@Component({
  selector: 'direct-assignment',
  template: `
  <div class="d-flex align-items-center">
    <button class="btn-blank" (click)="openAssignmentDialog()">
      <div class="editable-field empty">
        <i class="editable-field-icon fas fa-user tiny-right-buffer" *ngIf="!writerAssigned"></i>
        <span *ngIf="!writerAssigned" class="text-info">Assign preferred writer</span>
      </div>
    </button>
    <writer-avatar
      *ngIf="writerAssigned"
      class="tiny-right-buffer d-inline-block"
      [writer]="assignedWriter"
      [customerId]="assignedWriter.id"
      [size]="24"
      [hideName]="true"
      [transparentAvatar]="!writerAcceptedAssignment()"
      [linkToProfile]="false"
      borderRadius="50%"
      popover="Assigned to {{assignedWriter.displayName}}"
      triggers="mouseenter:mouseleave"
      (click)="openAssignmentDialog()">
    </writer-avatar>
    <button *ngIf="writerAssigned" (click)="openAssignmentDialog()" class="btn-blank">
      <span *ngIf="writerAcceptedAssignment()">Assigned to </span>
      <span>
        {{assignedWriter.displayName}}
      </span>
      <span class="badge badge-tertiary badge-pill" *ngIf="shouldShowAssignmentPending">
        Awaiting reply
      </span>

    </button>
    <red-dot
      *ngIf="writerAssigned"
      [show]="showWriterStatus(assignedWriter)"
      [dotColor]="getStatusColor(assignedWriter)"
      class="ml-2 d-block">
    </red-dot>
    <span
      *ngIf="writerAssigned && enabled"
      class="editable-field-remove tiny-left-buffer"
      (click)="$event.stopPropagation(); onRemoveAssignment()"></span>
    <pop-card
      *ngIf="preAssigningWriter"
      class="small-bottom-buffer-lg"
      (outsideClick)="closeAssignmentDialog()"
      [customStyle]="{'width': '325px'}">
      <div class="row no-gutters p-3">
        <div class="col-12">
          <input id="preferred-writer" type="text" class="form-control" placeholder="Search your preferred writers"
          [(ngModel)]="preferredWriterSearch"
          [ngModelOptions]="{standalone: true}"
          autofocus
          autocomplete="off"/>
        </div>
      </div>
      <div class="row no-gutters">
        <div class="col-12 writer-list">
          <writers-list
            [preferredWritersList]="elegibleWritersByCustomer"
            [customer]="customer"
            [(writerPreAssigned)]="writerPreAssigned"
            [(preAssignedWriter)]="preAssignedWriter"
            [section]="'this client'"
            [preferredWriterSearch]="preferredWriterSearch"
          ></writers-list>
          <writers-list
            [preferredWritersList]="elegibleWritersByAccount"
            [customer]="customer"
            [(writerPreAssigned)]="writerPreAssigned"
            [(preAssignedWriter)]="preAssignedWriter"
            [section]="'all clients'"
            [preferredWriterSearch]="preferredWriterSearch"
          ></writers-list>
        </div>
      </div>
      <div class="row no-gutters p-3">
        <div class="col-12">
          <button
            class="btn btn-secondary btn-block"
            (click)="onAssignWriter(preAssignedWriter)"
            [disabled]="!writerPreAssigned">Request this writer</button>
        </div>
        <div class="col-12">
          <hr>
        </div>
        <div class="col-12">
          <div class="d-flex flex-row">
            <div class="fas fa-question-circle text-muted mr-1"></div>
            <div class="small text-muted">
              Remember to allow extra time in case your preferred writer is unavailable for this request.
              <a style="display: none" href="{{linkTo('direct_assignment')}}" target="_blank">Learn more</a>
            </div>
          </div>
        </div>
      </div>
    </pop-card>
  </div>
  `
})

export class DirectAssignmentComponent implements OnInit{

  @Input() customer: ICustomer = null
  @Input() preassignedWriter: IWriter = null
  @Input() keyword: IKeyword = null
  @Input() enabled: boolean = true
  @Output() assignToWriter = new EventEmitter<IWriter>()
  @Output() removeAssignment = new EventEmitter<IWriter>()

  elegibleWritersByCustomer: IWriter[] = []
  elegibleWritersByAccount: IWriter[] = []
  preAssigningWriter: boolean = false
  writerAssigned: boolean = false
  writerPreAssigned: boolean = false
  preAssignedWriter: IWriter
  assignedWriter: IWriter
  preferredWriterSearch: string

  ngOnInit() {
    if (this.preassignedWriter) this.onAssignWriter(this.preassignedWriter)
  }

  get shouldShowAssignmentPending(): boolean {
    return this.keyword && this.keyword.state === 'assigned' && this.assignedWriter.id === this.keyword.writerId
  }

  writerAcceptedAssignment(): boolean {
    return this.keyword && this.preassignedWriter && this.keyword.state === 'active'
  }

  showWriterStatus(writer: IWriter): boolean {
    return this.isWriterUnavailable(writer) || (this.isPooledWriter(writer) && !this.isPreferredWriter(writer))
  }

  isWriterUnavailable(writer: IWriter): boolean {
    return writer.state === 'unavailable'
  }

  isPooledWriter(writer: IWriter): boolean {
    return this.customer.pooledWriters.some(w => w === writer)
  }

  isPreferredWriter(writer: IWriter): boolean {
    return this.customer.preferredWriters.some(w => w === writer)
  }

  getStatusColor(writer: IWriter): string {
    if (this.isWriterUnavailable(writer)) { return 'text-red' }
    if (this.isPooledWriter(writer) && !this.isPreferredWriter(writer)) { return 'text-secondary' }
  }

  onAssignWriter(chosenWriter: IWriter): void {
    this.assignedWriter = chosenWriter
    this.writerAssigned = true
    this.preAssigningWriter = false
    this.writerPreAssigned = false
    this.preAssignedWriter = null
    this.assignToWriter.emit(this.assignedWriter)
  }

  onRemoveAssignment(): void {
    this.assignedWriter = null
    this.writerAssigned = false
    this.preAssigningWriter = false
    this.writerPreAssigned = false
    this.preAssignedWriter = null
    this.removeAssignment.emit()
  }

  openAssignmentDialog(): void {
    if (this.enabled) {

      this.elegibleWritersByCustomer = this.customer.pooledWriters.concat(
        this.customer.preferredByCustomer
      ).sort(
        (a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
      ).filter((v,i,a) => a.findIndex(t => (t.id === v.id)) === i)

      this.elegibleWritersByCustomer.forEach(w => w.section = 'client')

      this.elegibleWritersByAccount = this.customer.preferredByAccount.sort((a, b) => a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()))
      this.elegibleWritersByAccount.forEach(w => w.section = 'account')
      if (this.assignedWriter) {
        this.writerPreAssigned = true
        this.preAssignedWriter = this.assignedWriter
      }
      this.preAssigningWriter = true
    }
  }

  closeAssignmentDialog(): void{
    this.preAssigningWriter = false
    this.preAssignedWriter = null
    this.writerPreAssigned = false
  }

  linkTo(externalRouteKey: string): string {
    return externalRoute(externalRouteKey)
  }
}
